import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { Map } from "immutable";
import ImmutablePropTypes from "react-immutable-proptypes";
import { Input, Button, message, Space } from "antd";
import { getAllSites } from "../../../actions/site_actions";
import { User } from "../../../records/account_records";
import DropdownPagesHeader from "../../common/DropdownPagesHeader";
import {
  getAccountPermissions,
  getPermissionsList,
  getAllAccounts,
  getDiagnoseProperties,
  clearInvitedUserError
} from "../../../actions/account_actions";
import ManageUsersTable from "./ManageUsersTable";
import InviteUserModal from "./InviteUserModal";
import Loading from "../../common/Loading.js";

class ManageUsers extends React.Component {
  static propTypes = {
    route: PropTypes.shape({
      currentUser: PropTypes.instanceOf(User).isRequired,
      allOtherUsers: ImmutablePropTypes.listOf(User).isRequired,
    }).isRequired,
    getAllAccounts: PropTypes.func.isRequired,
    getAccountPermissions: PropTypes.func.isRequired,
    getPermissionsList: PropTypes.func.isRequired,
  };

  state = {
    editingUserId: null,
    inviteModalOpen: false,
    userFilter: "",
    selectedRowKeys: [],
  };
  
  componentDidMount() {
    this.props.getAccountPermissions();
    this.props.getPermissionsList();
    this.props.getDiagnoseProperties();
    if (this.props.currentUser && (this.props.currentUser.accountId === 22 || this.props.currentUser.accountId === 375 || this.props.currentUser.accountId === 75)) {
      this.props.getAllAccounts();
      this.props.getAllSites({ allSites: true });
    }
  }

  setSelectedRowKeys = (selectedRowKeys) => {
    this.setState({ selectedRowKeys });
  };

  toggleInviteModal = () => {
    this.props.clearInvitedUserError();
    this.setState({ inviteModalOpen: !this.state.inviteModalOpen });
  };

  handleSeachUser = (e) => {
    this.setState({ userFilter: e.target.value.trim() });
  };

  updatingEditingUserId = (id) => {
    this.setState({ editingUserId: id });
  };

  escapeCSV = (value) => {
    if (typeof value !== 'string') {
      value = String(value || '');
    }
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  downloadSelectedUsersCSV = () => {
    if (this.state.selectedRowKeys.length === 0) {
      message.error('Please select at least one user to download');
      return;
    }

    const selectedUsers = this.props.users.filter(user => this.state.selectedRowKeys.includes(user.id));
    const account = this.props.allAccounts.find(a => a.accountId === this.props.currentUser.accountId);
    const accountName = account ? account.accountName : 'N/A';

    // CSV headers
    const headers = [
      'Name',
      'Email',
      'Admin Access',
      'Dialogue Properties',
      'Diagnose Properties',
      'Permissions'
    ];

    // CSV rows
    const rows = selectedUsers.map(user => {
      const role = user.isAdmin ? 'Yes' : 'No';

      let dialogueProps;
      if (user.allSiteAccess) {
        dialogueProps = 'All Access';
      } else if (!user.siteAccess || user.siteAccess.size === 0) {
        dialogueProps = 'No Access';
      } else {
        dialogueProps = user.siteAccess
          .map(id => this.props.sites.find(s => s.id === id)?.domain)
          .filter(Boolean)
          .join('; ');
      }

      let diagnoseProps;
      if (user.allDiagnoseSiteAccess) {
        diagnoseProps = 'All Access';
      } else if (!user.diagnoseAccess || user.diagnoseAccess.size === 0) {
        diagnoseProps = 'No Access';
      } else {
        diagnoseProps = user.diagnoseAccess
          .map(id => this.props.diagnoseProperties.find(p => p._id === id)?.name)
          .filter(Boolean)
          .join('; ');
      }

      let permissions;
      if (user.allFeatureAccess) {
        permissions = 'All Access';
      } else if (!user.featureAccess || user.featureAccess.size === 0) {
        permissions = 'No Access';
      } else {
        permissions = user.featureAccess
          .map(f => (typeof f === 'string' ? f : f.name))
          .filter(Boolean)
          .join('; ');
      }

      return [
        user.name,
        user.email,
        role,
        dialogueProps,
        diagnoseProps,
        permissions
      ];
    });

    // Convert to CSV string
    const csvContent = [
      ['Account Name:', accountName, 'Account ID:', this.props.currentUser.accountId],
      [],
      headers.map(h => this.escapeCSV(h)).join(','),
      ...rows.map(row => row.map(cell => this.escapeCSV(String(cell))).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `account_id_${this.props.currentUser.accountId}_users_data.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  render() {
    if (
      !this.props.users ||
      !this.props.currentUser ||
      (this.props.currentUser.accountId === 22 && !this.props.allAccounts.size)
    ) {
      return <Loading />;
    }
    return (
      <div>
        <DropdownPagesHeader name="Manage Users" />
        <div className="manage-users-container">
          <div className="header-container">
            <Input
              className="search-users"
              placeholder="Search users"
              value={this.state.userFilter}
              onChange={this.handleSeachUser}
            />
            <Space>
              <Button
                onClick={this.toggleInviteModal}
                className="invite-button"
                disabled={!!this.state.editingUserId}
              >
                + Invite User
              </Button>
              <Button
                type="primary"
                onClick={this.downloadSelectedUsersCSV}
                disabled={this.state.selectedRowKeys.length === 0}
                title={this.state.selectedRowKeys.length === 0 ? 'Select at least one user to download' : `Download CSV for ${this.state.selectedRowKeys.length} selected user(s)`}
              >
                Download User Data({this.state.selectedRowKeys.length})
              </Button>
            </Space>  
          </div>
          <ManageUsersTable
            userFilter={this.state.userFilter}
            editingUserId={this.state.editingUserId}
            setEditingUserId={this.updatingEditingUserId}
            currentUser={this.props.currentUser}
            sites={this.props.sites}
            siteGroups={this.props.siteGroups}
            permissionsList={this.props.permissionsList}
            diagnoseProperties={this.props.diagnoseProperties}
            getAllSites={this.props.getAllSites}
            getAllAccounts={this.props.getAllAccounts}
            allUsers={this.props.users}
            allAccounts={this.props.allAccounts}
            selectedRowKeys={this.state.selectedRowKeys}
            setSelectedRowKeys={this.setSelectedRowKeys}
          />
          <InviteUserModal
            inviteModalOpen={this.state.inviteModalOpen}
            toggleInviteModal={this.toggleInviteModal}
            currentUser={this.props.currentUser}
            sites={this.props.sites}
            siteGroups={this.props.siteGroups}
            permissionsList={this.props.permissionsList}
            diagnoseProperties={this.props.diagnoseProperties || []}
            invitedUser={this.props.invitedUser}
            pendingRequestsMap={this.props.pendingRequestsMap}
            invitedUserError={this.props.invitedUserError}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = function (store) {
  return {
    currentUser: store.accountState.getIn(["userDetails", "value"]),
    sites: store.siteState.getIn(["sites", "value"]),
    siteGroups: store.siteState.getIn(["siteGroups", "value"]),
    permissionsList: store.accountState.getIn(["permissionsList", "value"]),
    diagnoseProperties: store.accountState.getIn(["diagnoseProperties", "value"]),
    users: store.accountState.getIn(["allUsers", "value"]),
    allAccounts: store.accountState.getIn(["allAccounts", "value"]),
    invitedUser: store.accountState.getIn(["invitedUser", "value"]),
    invitedUserError: store.accountState.getIn(["invitedUser", "error"]),
    pendingRequestsMap: Map({
      sites: store.siteState.getIn(["sites", "pending"]),
      invitedUser: store.accountState.getIn(["invitedUser", "pending"]),
      allAccountsPending: store.accountState.getIn(["allAccounts", "pending"]),
    }),
  };
};

export default connect(mapStateToProps, {
  getAllSites,
  getAllAccounts,
  getAccountPermissions,
  getPermissionsList,
  getDiagnoseProperties,
  clearInvitedUserError
})(ManageUsers);
